import { Bike, Store, ArrowUpRight, Mail } from "lucide-react";
import { MaskedLines, FadeIn, Reveal, SectionHead } from "@/components/Motion";
import CTA from "@/components/CTA";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const PLANS = [
  {
    id: "partner",
    icon: Bike,
    badge: "Limited-time founding pricing",
    name: "Founding Partner",
    price: "$99",
    per: "one-time setup",
    dark: true,
    features: [
      "Enrollment and activation in the partner program",
      "Your first zone included",
      "One full year of participation included",
      "Branded shop profile in the VeloPaths Rider app",
      "50/50 revenue share on every advertiser in your zones",
      "Additional zones available for an additional fee — pricing varies by traffic and revenue potential",
    ],
    cta: "Become a Founding Partner",
    testid: "btn-pricing-partner",
  },
  {
    id: "advertiser",
    icon: Store,
    badge: "Limited-time founding pricing",
    name: "Founding Advertiser",
    price: "$99",
    per: "per year · per location",
    dark: false,
    features: [
      "Advertiser pin placement within an eligible zone",
      "Listing visibility in the VeloPaths Rider app",
      "Reach cyclists near bike paths and destinations",
      "Rider deals, photos, and offerings on your listing",
      "Renewal pricing may differ in future years as the platform expands and additional features are introduced",
    ],
    cta: "Become a Founding Advertiser",
    testid: "btn-pricing-advertiser",
  },
];

const FAQS = [
  {
    q: "What is included in the $99 Founding Partner setup?",
    a: "Enrollment and activation in the partner program, your first zone, and one full year of participation. It's a one-time fee — paid once, not annually — and it does not include any additional zone costs.",
  },
  {
    q: "What is a zone?",
    a: "A zone is a leasable cycling territory — a mapped area of bike paths and destinations where your approved advertisers appear as curated stops in the VeloPaths Rider app.",
  },
  {
    q: "Can I purchase additional zones?",
    a: "Yes. Additional zones can be added at any time for an additional annual fee per zone. Pricing varies based on traffic and revenue potential, and zones are assigned by VeloPaths Admin after approval — it's a curated process, not an instant checkout.",
  },
  {
    q: "How much does advertiser participation cost?",
    a: "Founding Advertiser pricing is $99 per year per location. That includes pin placement within an eligible zone and full listing visibility in the VeloPaths Rider app.",
  },
  {
    q: "Will pricing ever change?",
    a: "Founding Partner and Founding Advertiser pricing is available for a limited time. Renewal pricing may differ in future years as the platform expands and additional features are introduced.",
  },
  {
    q: "What is founding pricing?",
    a: "Founding pricing is our preferred early-adopter rate during the initial growth phase of the VeloPaths platform — the lowest price these programs will ever be offered at, reserved for the businesses and shops that join first.",
  },
];

export default function Pricing() {
  return (
    <main data-testid="page-pricing">
      <section className="relative overflow-hidden bg-[#0B2B22] pt-40 pb-20 sm:pb-24">
        <div className="grain absolute inset-0" />
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <FadeIn delay={0.05}>
            <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#7FB998]">
              Pricing
            </p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#F4F7F4]">
            <MaskedLines lines={["Simple Founding Pricing.", "$99 to Start."]} />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F7F4]/75">
              Founding Partner and Founding Advertiser pricing is available for a limited time.
              Early adopters receive preferred pricing during the initial growth phase of the
              VeloPaths platform.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 sm:py-28 lg:px-16">
        <div className="grid gap-8 lg:grid-cols-2">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`pricing-card-${p.id}`}
                className={`flex h-full flex-col rounded-[2rem] p-8 sm:p-10 ${
                  p.dark
                    ? "bg-[#0B2B22] text-[#F4F7F4]"
                    : "border border-[#154234]/12 bg-white text-[#0B2B22]"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={`grid size-12 place-items-center rounded-2xl ${
                      p.dark ? "bg-[#7FB998]/15 text-[#7FB998]" : "bg-[#2D6A4F]/10 text-[#2D6A4F]"
                    }`}
                  >
                    <p.icon size={21} />
                  </span>
                  <span className="rounded-full bg-[#E05A47] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                    {p.badge}
                  </span>
                </div>
                <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">{p.name}</h2>
                <p className="mt-4">
                  <span className="font-mono text-5xl font-bold">{p.price}</span>
                  <span className={p.dark ? "text-[#F4F7F4]/60" : "text-[#52635B]"}> {p.per}</span>
                </p>
                <ul className="mt-7 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                      <span className={p.dark ? "text-[#F4F7F4]/80" : "text-[#52635B]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={p.testid}
                  className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-colors duration-300 ${
                    p.dark
                      ? "bg-[#E05A47] text-white hover:bg-[#c94a39]"
                      : "bg-[#0B2B22] text-[#F4F7F4] hover:bg-[#154234]"
                  }`}
                >
                  {p.cta} <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-10 max-w-2xl text-center font-mono text-xs uppercase tracking-[0.2em] leading-relaxed text-[#52635B]">
            Renewal pricing may differ in future years as the platform expands and additional
            features are introduced.
          </p>
        </Reveal>
      </section>

      <section data-testid="section-pricing-faq" className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <SectionHead overline="FAQ" title="Founding pricing questions, answered." />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06} className="h-full">
                <div className="h-full rounded-[1.75rem] border border-[#154234]/10 bg-[#F4F7F4] p-7">
                  <h3 className="font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                    {f.q}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 text-sm text-[#52635B]">
              More questions? Email{" "}
              <a
                href="mailto:info@velopaths.com"
                data-testid="btn-pricing-contact"
                className="-my-2 inline-flex items-center py-2 font-bold text-[#2D6A4F] underline underline-offset-4 hover:text-[#E05A47]"
              >
                info@velopaths.com
              </a>{" "}
              — we answer personally, usually within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <CTA
        overline="Limited-time founding pricing"
        title="Get in at the founding rate."
        sub="Join as a Founding Partner or Founding Advertiser while early-adopter pricing lasts."
        primary={{ label: "Become a Founding Partner", to: PORTAL_URL, testid: "btn-cta-founding-partner" }}
        secondary={{ label: "Become a Founding Advertiser", to: PORTAL_URL, testid: "btn-cta-founding-advertiser" }}
      />
    </main>
  );
}
